/**
 * PIN Protection Component
 * Restricts access to the Analytics Engine with a 6-digit PIN
 */

import React, { useState, useEffect, useRef } from 'react';
import { Lock, Eye, EyeOff, AlertCircle, Shield } from 'lucide-react';

const CORRECT_PIN = '440134';
const SESSION_KEY = 'analytics_pin_verified';
const ATTEMPT_LIMIT = 5;
const LOCKOUT_DURATION = 30000; // 30 seconds

const PinProtection = ({ children }) => {
  const [pin, setPin] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);
  const inputRef = useRef(null);

  // Check session storage on mount
  useEffect(() => {
    const verified = sessionStorage.getItem(SESSION_KEY);
    if (verified === 'true') {
      setIsVerified(true);
    }
    // Focus input on mount
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Handle lockout countdown
  useEffect(() => {
    if (isLocked && lockoutRemaining > 0) {
      const timer = setInterval(() => {
        setLockoutRemaining(prev => {
          if (prev <= 1000) {
            setIsLocked(false);
            setAttempts(0);
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isLocked, lockoutRemaining]);

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPin(value);
    setError('');

    // Auto-submit when 6 digits are entered
    if (value.length === 6) {
      verifyPin(value);
    }
  };

  const verifyPin = (pinToVerify) => {
    if (isLocked) return;

    if (pinToVerify === CORRECT_PIN) {
      sessionStorage.setItem(SESSION_KEY, 'true');
      setIsVerified(true);
      setError('');
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setPin('');

      if (newAttempts >= ATTEMPT_LIMIT) {
        setIsLocked(true);
        setLockoutRemaining(LOCKOUT_DURATION);
        setError(`Too many attempts. Locked for ${LOCKOUT_DURATION / 1000} seconds.`);
      } else {
        setError(`Incorrect PIN. ${ATTEMPT_LIMIT - newAttempts} attempts remaining.`);
      }

      // Refocus input
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pin.length === 6) {
      verifyPin(pin);
    } else {
      setError('Please enter a 6-digit PIN');
    }
  };

  const handleKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 46, 9, 27, 13].includes(e.keyCode)) {
      return;
    }
    // Allow: Ctrl+A, Ctrl+C, Ctrl+V
    if (e.ctrlKey && [65, 67, 86].includes(e.keyCode)) {
      return;
    }
    // Block non-numeric keys
    if ((e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  if (isVerified) {
    return children;
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Analytics Engine</h1>
          <p className="text-gray-400">Enter your PIN to access the dashboard</p>
        </div>

        {/* PIN Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-gray-800 rounded-xl p-6 shadow-xl border border-gray-700">
            {/* PIN Input */}
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-medium text-gray-300">
                  Security PIN
                </label>
              </div>

              <div className="relative">
                <input
                  ref={inputRef}
                  type={showPin ? 'text' : 'password'}
                  value={pin}
                  onChange={handlePinChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter 6-digit PIN"
                  disabled={isLocked}
                  className={`
                    w-full px-4 py-4 text-center text-2xl font-mono tracking-[0.5em]
                    bg-gray-900 border rounded-lg
                    text-white placeholder-gray-500
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${error ? 'border-red-500' : 'border-gray-600'}
                  `}
                  maxLength={6}
                  autoComplete="off"
                />

                {/* Toggle visibility button */}
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {/* PIN dots indicator */}
              <div className="flex justify-center gap-2 mt-4">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      i < pin.length
                        ? 'bg-blue-500'
                        : 'bg-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="mt-4 flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Lockout countdown */}
            {isLocked && lockoutRemaining > 0 && (
              <div className="mt-4 text-center">
                <p className="text-yellow-400 text-sm">
                  Try again in {Math.ceil(lockoutRemaining / 1000)} seconds
                </p>
              </div>
            )}
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={pin.length !== 6 || isLocked}
            className={`
              w-full py-4 rounded-lg font-semibold text-white
              transition-all duration-200
              ${pin.length === 6 && !isLocked
                ? 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                : 'bg-gray-700 cursor-not-allowed opacity-50'
              }
            `}
          >
            {isLocked ? 'Locked' : 'Access Dashboard'}
          </button>
        </form>

        {/* Footer hint */}
        <p className="text-center text-gray-500 text-sm mt-6">
          Contact an administrator if you've forgotten the PIN
        </p>
      </div>
    </div>
  );
};

export default PinProtection;
